import { FC, useCallback, useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import Loader from "shared/loader/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./MainPage.module.css";
import UgskLogo from "shared/assets/UgskLogo";
import { RoutesType } from "shared/types/routes/routers-type";
import { useAppDispatch, useAppSelector } from "shared/utils/hooks/useRedux";
import {
  fetchDictionaries,
  fetchProductAlias,
} from "entities/main-page/action/fetch-main-page";
import {
  ProductID,
  SellerID,
  Error,
  ProductAliasState,
} from "entities/main-page/selector";
import { DefaultId } from "entities/mite-policy/step-1/selector";

const url = "https://max.ru/ugoria24_bot";
const encodeBase64Url = (value: string): string => {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const MainPage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [deepLink, setDeepLink] = useState<string>("");
  const [deepLinkProductSales, setDeepLinkProductSales] = useState<string>("");
  const [errorQR, setErrorQR] = useState<boolean>(false);
  const searchParams = new URLSearchParams(location.search);
  const productId = searchParams.get("productId") || "";
  const sellerID = searchParams.get("sellerId") || "";
  const ID = searchParams.get("id") || "";
  const sellerIDDictionaries = useAppSelector(SellerID);
  const productIDDictionaries = useAppSelector(ProductID);
  const errorDictionaries = useAppSelector(Error);
  const defaultId = useAppSelector(DefaultId);
  const { isLoadingProductAlias, errorProductAlias, ProductAlias, ProductPrintName } =
    useAppSelector(ProductAliasState);

  const isSellerFlow = Boolean(sellerID);
  const isProductFlow = Boolean(!sellerID && ID);
  const resolvedProductAlias = isSellerFlow ? "Антиклещ" : ProductAlias;

  useEffect(() => {
    if (!ID && !sellerID) {
      setErrorQR(true);
      setLoading(false);
      return;
    }
    setErrorQR(false);
  }, [ID, sellerID]);

  useEffect(() => {
    if (!isProductFlow) {
      return;
    }
    dispatch(fetchProductAlias({ agentSalesDataID: ID }));
  }, [isProductFlow, ID, dispatch]);

  useEffect(() => {
    const shouldFetchDictionaries =
      isSellerFlow || (isProductFlow && ProductAlias === "Антиклещ");
    if (!shouldFetchDictionaries) {
      return;
    }
    dispatch(
      fetchDictionaries({ SellerId: sellerID, Id: ID, DefaultId: defaultId })
    );
  }, [
    isSellerFlow,
    isProductFlow,
    ProductAlias,
    sellerID,
    ID,
    defaultId,
    dispatch,
  ]);
  const parseUrlParams = useCallback(() => {
    if (isProductFlow && isLoadingProductAlias) {
      setLoading(true);
      return;
    }
    if (isProductFlow && errorProductAlias.length > 0) {
      setDeepLink("");
      setDeepLinkProductSales("");
      setLoading(false);
      return;
    }
    if (isProductFlow && ProductAlias === "Онкозащита") {
      const productSalesParams = JSON.stringify([ID, 0]);
      const base64ProductSales = encodeBase64Url(productSalesParams);
      setDeepLinkProductSales(`${url}?start=${base64ProductSales}`);
      setDeepLink("");
      setLoading(false);
      return;
    }
    setDeepLinkProductSales("");
    let params = "";
    if (sellerID && ID) {
      params = `sellerId-${sellerID}_id-${ID}`;
    } else if (ID) {
      params = `id-${ID}`;
    }
    if (!params) {
      setDeepLink(url);
      setLoading(false);
      return;
    }
    const base64Params = encodeBase64Url(params);
    const newDeepLink = `${url}?start=${base64Params}`;
    setDeepLink(newDeepLink);
    if (!isSellerFlow) {
      setLoading(false);
    }
  }, [
    sellerID,
    ID,
    isSellerFlow,
    isProductFlow,
    ProductAlias,
    isLoadingProductAlias,
    errorProductAlias,
  ]);

  useEffect(() => {
    parseUrlParams();
  }, [parseUrlParams]);

  useEffect(() => {
    if (!isSellerFlow) {
      return;
    }
    if (sellerIDDictionaries && productIDDictionaries) {
      setLoading(false);
    }
  }, [isSellerFlow, sellerIDDictionaries, productIDDictionaries]);

  const handleGoToMax = () => {
    if (loading) {
      return;
    }
    if (resolvedProductAlias === "Онкозащита" && deepLinkProductSales) {
      window.location.href = deepLinkProductSales;
      return;
    }
    if (deepLink) {
      window.location.href = deepLink;
    }
  };

  const handleGoToPage = () => {
    if (loading) {
      return;
    }
    if (resolvedProductAlias === "Антиклещ") {
      navigate(RoutesType.MitePolicy, {
        state: {
          sellerID: sellerIDDictionaries,
          productId: productIDDictionaries,
          id: ID,
          productIdUrl: productId,
        },
      });
      return;
    }
    if (resolvedProductAlias === "Онкозащита") {
      navigate(RoutesType.CancerProtection, {
        state: {
          AgentSalesDataID: ID,
        },
      });
    }
    if (resolvedProductAlias === "ВашеЗдоровье") {
      navigate(RoutesType.HealthAL, {
        state: {
          AgentSalesDataID: ID,
        },
      });
    }
  };

  if (errorQR || errorProductAlias.length > 0) {
    return (
      <Box className={styles.mainBox}>
        <UgskLogo />

        <Box className={styles.errorBox}>
          <p>Некорректный QR-код</p>
        </Box>
      </Box>
    );
  }

  if (errorDictionaries[0] === "Некорректный QR-код") {
    return (
      <Box className={styles.mainBox}>
        <UgskLogo />

        <Box className={styles.errorBox}>
          <p>{errorDictionaries[0]}</p>
        </Box>
      </Box>
    );
  }

  if (
    resolvedProductAlias === "Антиклещ" ||
    resolvedProductAlias === "Онкозащита"
  ) {
    return (
      <Box className={styles.mainBox}>
        <UgskLogo />
        <Box className={styles.container}>
          {loading ? (
            <Loader title={"Загрузка... Это займет несколько секунд."} />
          ) : (
            <Box className={styles.btnBox}>
              <h1 className={styles.title}>
                Оформление полиса {resolvedProductAlias}
              </h1>
              <Button
                className={styles.gradientButton}
                onClick={handleGoToMax}
                disabled={loading}
              >
                Перейти в Max
              </Button>

              <Button
                className={styles.outlineButton}
                onClick={handleGoToPage}
                disabled={loading}
              >
                Оформить на сайте
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (resolvedProductAlias === "ВашеЗдоровье") {
    return (
      <Box className={styles.mainBox}>
        <UgskLogo />
        <Box className={styles.container}>
          {loading ? (
            <Loader title={"Загрузка... Это займет несколько секунд."} />
          ) : (
            <Box className={styles.btnBox}>
              <h1 className={styles.title}>
                Оформление полиса {ProductPrintName}
              </h1>

              <Button
                className={styles.gradientButton}
                onClick={handleGoToPage}
                disabled={loading}
              >
                Оформить на сайте
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  return null;
};
